import { inject, Injectable, signal, computed } from '@angular/core';

import { Observable, shareReplay, tap } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { AuthResourceService } from '../../app/api/mobile-app-be/api/authResource.service';
import { ClientiFoTokenResponse } from '../../app/api/mobile-app-be/model/clientiFoTokenResponse';

interface JwtTokenPayload {
  codiciClienti: string[];
  exp?: number;
}

const JWT_TOKEN_KEY = 'jwtTokenKey';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authResourceService = inject(AuthResourceService);

  readonly token = signal<string | null>(sessionStorage.getItem(JWT_TOKEN_KEY));

  readonly codiciClienti = computed<string[]>(() => {
    const token = this.token();
    if (!token) {
      return [];
    }

    return jwtDecode<JwtTokenPayload>(token).codiciClienti ?? [];
  });

  readonly isAuthenticated = computed<boolean>(() => this.isTokenValid(this.token()));

  private jwtToken$: Observable<ClientiFoTokenResponse> | null = null;

  public retrieveJWTTokenFromXToken(xSodToken: string): Observable<ClientiFoTokenResponse> {
    if (!this.jwtToken$) {
      this.jwtToken$ = this.authResourceService.mobileAppBeAuthClientiFoTokenPost(xSodToken).pipe(
        tap((response: ClientiFoTokenResponse) => {
          const accessToken = response.access_token ?? null;
          if (accessToken) {
            sessionStorage.setItem(JWT_TOKEN_KEY, accessToken);
          }
          this.token.set(accessToken);
        }),
        shareReplay(1)
      );
    }

    return this.jwtToken$;
  }

  public clearToken(): void {
    sessionStorage.removeItem(JWT_TOKEN_KEY);
    this.jwtToken$ = null;
    this.token.set(null);
  }

  private isTokenValid(token: string | null): boolean {
    if (!token) {
      return false;
    }

    try {
      const { exp } = jwtDecode<JwtTokenPayload>(token);
      return typeof exp === 'number' && exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

}
